import '../accordion/accordion';
import {computed, onBeforeUnmount, onMounted, reactive, ref, toRefs,} from 'vue';
import StickyScroller from '../../scripts/lib/sticky-scroller';
import {trackPdpElementSeen} from '../tracking/trackingLayer';
import EventBus from '../../scripts/lib/EventBus.js';
import {mediaQuery} from '../../scripts/lib/mediaQuery.js';
import {readJsonFromDom} from '../../scripts/utils.js';
import '../drawer/drawer';

const templateElement = document.getElementById('product-detail-accordion-template');
const manufacturerData = readJsonFromDom('#entsManufacturerData');

export default {
  template: templateElement ? templateElement.innerHTML : '',

  props: {
    activeArticle: {
      type: Object,
      required: true
    },
    image: {
      type: Array,
      required: true,
    },
    productDetails: {
      type: Object,
      required: true,
    },
    isPartnerArticle: {
      type: Boolean,
      required: true,
      default: false,
    },
    specialAttributesByHeadline: {
      type: Object,
      required: false,
      default: null,
    },
    deliveryOptions: {
      type: Object,
      required: false,
      default: null,
    },
    showValuePackHint: {
      type: Boolean,
      required: false,
      default: false,
    },
    manufacturerSpecificationKey: {
      type: String,
      required: false,
      default: null,
    },
    exceedsMinimumOrderValue: {
      type: Boolean,
      required: true,
      default: false,
    },
    isOversizedShipping: {
      type: Boolean,
      required: false,
      default: false,
    },
    brandName: {
      type: String,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const isDesktopViewport = ref(mediaQuery.L_and_up);
    const drawer = ref(null);
    const shownManufacturerData = ref(null);
    let accordionObserver = null;
    let resizeObserver = null;

    const state = reactive({
      showReadMoreValue: true,
      activateReadMore: true,
      imageTrackingSent: [],
      timers: [],
      accordionTrackingSent: [],
      visibleAccordions: [],
    });

    const imageAltTag = computed(() => {
      return `${props.brandName} ${props.productName}${props.activeArticle.colors?.aktiv?.name ? ': ' + props.activeArticle.colors.aktiv.name : ''}`.trim();
    });

    const updateViewport = () => {
      isDesktopViewport.value = mediaQuery.L_and_up;
    };

    onMounted(() => {
      resizeObserver = new ResizeObserver(() => {
        updateViewport();
      });
      resizeObserver.observe(document.documentElement);

      loadIcons();

      new StickyScroller('.ents-product-detail-accordion__content--inner', '.ents-product-detail-accordion__wrapper--sticky'); // NOSONAR

      observeAndTrackAccordions();
      window.addEventListener('pageshow', resetTrackingInObserver);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('pageshow', resetTrackingInObserver);
    });

    function resetTrackingInObserver(e) {
      if (e.persisted) {
        state.accordionTrackingSent = [];
      }
    }

    function loadIcons() {
      setTimeout(() => {
        const lazyIcons = document.querySelectorAll('.ents-product-detail-accordion__pflegehinweise [data-bewe-lazyload]');

        if (lazyIcons.length > 0) {
          lazyIcons.forEach((icon) => {
            const dataSource = icon.getAttribute('data-src');
            icon.setAttribute('src', dataSource);
            icon.addEventListener('load', () => {
              icon.removeAttribute('data-bewe-lazyload');
            });
          });
        }
      }, 50);
    }

    function openManufacturerSpecification() {
      shownManufacturerData.value = manufacturerData[props.manufacturerSpecificationKey];
      drawer.value.show();
    }

    function openCertificateDrawer(certifiedColumn) {
      EventBus.emit('ents:vue:certificate:drawer:open', certifiedColumn);
    }

    function observeAndTrackAccordions() {
      setTimeout(() => {
        if (accordionObserver !== null) {
          accordionObserver.disconnect();
          state.accordionTrackingSent = [];
        }
        const accordionInnerContentContainers = document.querySelectorAll('[data-ents-accordion-key]');
        const options = {
          root: null,
          rootMargin: '0px',
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        };

        accordionObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => compareAccordionVisibilityAndTrack(entry));
        }, options);

        Array.from(accordionInnerContentContainers)
          .forEach((el) => {
            accordionObserver.observe(el);
          });
      }, 50);
    }

    function compareAccordionVisibilityAndTrack(entryFromIntersectionObserver) {
      const el = entryFromIntersectionObserver.target;

      const accordionKey = `${el.getAttribute('data-ents-accordion-key')}`;
      const isAccordionContainer = el.className.includes('ents-product-detail-accordion-container');
      const accordionIsOpen = el.parentNode.getAttribute('open') !== null;

      if (entryFromIntersectionObserver.isIntersecting
        && (accordionIsOpen || isAccordionContainer)
        && !state.accordionTrackingSent[accordionKey]) {
        state.visibleAccordions[accordionKey] = true;
        // cancel tracking event if there is a new one within 150ms
        if (state.timers[accordionKey]) {
          clearTimeout(state.timers[accordionKey]);
        }
        state.timers[accordionKey] = setTimeout(() => {
          if (state.visibleAccordions[accordionKey]
            && entryFromIntersectionObserver.isIntersecting) {
            trackPdpElementSeen(accordionKey);
            state.accordionTrackingSent[accordionKey] = true;
          }
        }, 2000);
      } else {
        state.visibleAccordions[accordionKey] = false;
      }
    }

    function generateAltText(src, message) {
      let fileName = src.split('/')
        .pop()
        .split('.')[0];
      fileName = fileName.replace(/_size=\d+$/, '');
      let readableName = fileName.replace(/_/g, ' ');
      readableName = readableName.replace(/^carrier\s+/, '');
      return message.replace('{0}', readableName);
    }

    function triggerZoom() {
      EventBus.emit('bewerten:vue:image:zoom:reset-and-open', true);
    }

    const standardUrl = computed(() => {
      if (props.image.length >= 1) {
        return props.image[0].fallbackStandardUrl;
      }
      return '';
    });

    const zoomUrl = computed(() => {
      if (props.image.length >= 1) {
        return props.image[0].zoomRetinaUrl;
      }
      return '';
    });

    const srcset = computed(() => {
      if (props.image.length === 0) {
        return '';
      }

      return [
        [props.image[0].thumbnailUrl, '144w'],
        [props.image[0].thumbnailRetinaUrl, '288w'],
        [props.image[0].standardUrl, '435w'],
        [props.image[0].standardRedesignUrl, '680w'],
        [props.image[0].standardRetinaUrl, '870w'],
        [props.image[0].zoomUrl, '1122w'],
        [props.image[0].standardRedesignRetinaUrl, '1360w'],
        [props.image[0].zoomRetinaUrl, '2244w'],
      ]
        // remove non existent urls
        .filter(([src]) => !!src)
        // build srcset string
        .map(([src, size]) => `${src} ${size}`)
        .join(',\n');
    });

    const activeArticle = computed(() => props.activeArticle);

    return {
      ...toRefs(state),
      generateAltText,
      triggerZoom,
      standardUrl,
      zoomUrl,
      srcset,
      isDesktopViewport,
      openCertificateDrawer,
      openManufacturerSpecification,
      drawer,
      shownManufacturerData,
      activeArticle,
      imageAltTag
    };
  },
};
